<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="App Versions"
      subtitle="Manage mobile application versions for iOS and Android."
      :headers="headers"
      :items="versions"
      :loading="loading"
      show-search
      search-placeholder="Search versions..."
      show-add
      add-label="Add Version"
      @add="openAddDialog"
    >
      <template v-slot:item.platform="{ item }">
        <v-chip
          small
          label
          :color="item.platform === 'ios' ? 'blue-grey darken-3' : 'green darken-1'"
          text-color="white"
          class="font-weight-bold"
        >
          <v-icon left small v-if="item.platform === 'ios'">mdi-apple</v-icon>
          <v-icon left small v-else>mdi-android</v-icon>
          {{ item.platform.toUpperCase() }}
        </v-chip>
      </template>

      <template v-slot:item.force_update="{ item }">
        <v-chip
          small
          label
          :color="item.force_update ? 'red lighten-4' : 'blue-grey lighten-5'"
          :text-color="item.force_update ? 'red darken-2' : 'blue-grey darken-2'"
          class="font-weight-bold"
        >
          {{ item.force_update ? 'Forced' : 'Optional' }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div style="display:flex;gap:8px;align-items:center;">
          <PremiumActionButton
            icon="mdi-content-copy"
            label="Duplicate"
            @click="openDuplicateDialog(item)"
          />
          <PremiumActionButton
            icon="mdi-pencil-outline"
            label="Edit"
            @click="openEditDialog(item)"
          />
          <PremiumActionButton
            icon="mdi-delete-outline"
            label="Delete"
            variant="danger"
            @click="openDelete(item._id || item.id)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- Add/Edit Version Dialog -->
    <PremiumModalForm
      v-model="versionDialog"
      :title="isEditing ? 'Edit App Version' : isDuplicating ? 'Duplicate App Version' : 'Add App Version'"
      :submit-text="isEditing ? 'Update Version' : 'Save Version'"
      cancel-text="Cancel"
      :loading="saving"
      max-width="500"
      @submit="saveVersion"
    >
      <div style="display:flex;flex-direction:column;gap:18px;">
        <PremiumInput
          v-model="form.platform"
          name="platform"
          label="Platform *"
          is-select
          :items="platforms"
          v-validate="'required'"
          :error="errors.first('platform') || serverSideErrors.platform"
        />
        <PremiumInput
          v-model="form.version"
          name="version"
          label="Version Number *"
          placeholder="e.g. 1.0.5"
          v-validate="'required'"
          :error="errors.first('version') || serverSideErrors.version"
        />
        <PremiumInput
          v-model="form.message"
          name="message"
          label="Update Message"
          placeholder="What's new in this version?"
          is-textarea
          rows="3"
        />
        <div class="d-flex align-center mt-2 px-1">
            <v-switch
              v-model="form.force_update"
              color="error"
              inset
              hide-details
              class="mt-0"
            ></v-switch>
            <div class="ml-3">
                <div class="text-subtitle-2 font-weight-bold" style="color:var(--text-primary)">Force Update</div>
                <div class="text-caption text--secondary">Users will be required to update before using the app</div>
            </div>
        </div>
      </div>
    </PremiumModalForm>

    <!-- Delete Confirm Modal -->
    <ConfirmModal
      :isOpen="deleteDialog"
      type="danger"
      title="Delete Version"
      message="Are you sure you want to delete this version record?"
      confirmText="Yes, Delete"
      cancelText="Cancel"
      @confirm="deleteVersion"
      @cancel="deleteDialog = false"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import PremiumTable from '../common/PremiumTable.vue';
import ConfirmModal from '../common/ConfirmModal.vue';
import PremiumModalForm from '../common/PremiumModalForm.vue';
import PremiumActionButton from '../common/PremiumActionButton.vue';
import PremiumInput from '../common/PremiumInput.vue';

export default {
  name: 'AppVersionManagement',
  components: { PremiumTable, ConfirmModal, PremiumModalForm, PremiumActionButton, PremiumInput },

  data() {
    return {
      loading:        false,
      saving:         false,
      versions:       [],
      versionDialog:  false,
      isEditing:      false,
      isDuplicating:  false,
      deleteDialog:   false,
      delete_id:      null,
      form:           { id: null, platform: '', version: '', message: '', force_update: false },
      serverSideErrors: {},

      platforms: [
        { label: 'iOS',     value: 'ios' },
        { label: 'Android', value: 'android' },
      ],

      headers: [
        { text: 'Platform',     value: 'platform',      width: '15%' },
        { text: 'Version',      value: 'version',       width: '15%' },
        { text: 'Type',         value: 'force_update',  width: '15%' },
        { text: 'Update Message', value: 'message',     width: '40%' },
        { text: 'Actions',      value: 'actions',       width: '15%', sortable: false },
      ],
    };
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    getVersions() {
      this.loading = true;
      this.$axios.get('superadmin/app-versions').then(res => {
        this.loading = false;
        const data = res.data;
        // Handle both array response and {status, data} response
        if (Array.isArray(data)) {
          this.versions = data;
        } else if (data.status !== false) {
          this.versions = data.versions || data.data || data || [];
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message || 'Failed to load versions.' });
        }
      }).catch(err => {
        this.loading = false;
        const msg = err.response?.data?.message || 'Failed to load versions.';
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: msg });
      });
    },

    openAddDialog() {
      this.isEditing = false;
      this.isDuplicating = false;
      this.form = { id: null, platform: '', version: '', message: '', force_update: false };
      this.serverSideErrors = {};
      this.$validator.reset();
      this.versionDialog = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.isDuplicating = false;
      this.form = { 
          id:           item._id?.toString() || item.id, 
          platform:     item.platform, 
          version:      item.version, 
          message:      item.message, 
          force_update: !!item.force_update 
      };
      this.serverSideErrors = {};
      this.$validator.reset();
      this.versionDialog = true;
    },

    openDuplicateDialog(item) {
      this.isEditing = false;
      this.isDuplicating = true;
      this.form = {
          id:           null,
          platform:     item.platform,
          version:      item.version,
          message:      item.message,
          force_update: !!item.force_update,
      };
      this.serverSideErrors = {};
      this.$validator.reset();
      this.versionDialog = true;
    },

    saveVersion() {
      this.$validator.validateAll().then(valid => {
        if (!valid) return;
        this.saving = true;
        this.serverSideErrors = {};

        const method = this.isEditing ? 'put' : 'post';
        const url    = this.isEditing ? `/superadmin/app-versions/${this.form.id}` : '/superadmin/app-versions';

        this.$axios[method](url, this.form).then(res => {
          this.saving = false;
          const data = res.data;

          // API returned status:false in a 2xx response
          if (data && data.status === false) {
            this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 4000, message: data.message || 'Operation failed.' });
            return;
          }

          this.versionDialog = false;
          this.getVersions();
          this.setAlert({
            showAlert: true,
            variant:   'green accent-4',
            timeout:   3000,
            message:   `Version ${this.isEditing ? 'updated' : 'saved'} successfully.`,
          });
        }).catch(err => {
          this.saving = false;
          // Server returned a 4xx/5xx with a JSON body
          const data = err?.response?.data;
          const msg  = data?.message || data?.error || err?.message || 'Something went wrong.';

          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 4000, message: msg });

          // Handle field-level validation errors
          if (data?.errors && Array.isArray(data.errors)) {
            data.errors.forEach(e => { this.$set(this.serverSideErrors, e.field, e.message); });
          }
        });
      });
    },

    openDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteVersion() {
      const id = this.delete_id;
      this.deleteDialog = false;
      this.$axios.delete('superadmin/app-versions/' + id).then(res => {
        const data = res.data;
        if (data && data.status === false) {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message || 'Failed to delete version.' });
          return;
        }
        this.getVersions();
        this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: data.message || 'Version deleted successfully.' });
      }).catch(err => {
        const msg = err.response?.data?.message || 'Failed to delete version.';
        this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: msg });
      });
    },
  },

  mounted() {
    this.getVersions();
  },
};
</script>

<style scoped>
/* Standard switch is often fine, but we ensure it matches the premium aesthetic */
 .v-input--switch__track {
  opacity: 0.15 !important;
}
</style>
